import { gql } from "@apollo/client";
import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Text,
} from "@chakra-ui/react";
import { Form, Formik } from "formik";
import React from "react";
import { GenericError } from "src/components/Feedback/GenericError";
import { FormikSearchSelect } from "src/components/Inputs/SearchSelect";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import * as FSR from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { UseOrganizationType } from "src/hooks/useOrganization";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import { formatIsoDateToOrgDate } from "src/services/format";
import { validateWithZod } from "src/services/formValidations";
import * as GQL from "src/types/graphql";
import { z } from "zod";
import {
  buildRuleImportErrors,
  RuleImportError,
  zodIssuesToRuleImportErrors,
} from "./helpers";
import { RuleImportExportSchema } from "./ruleSchema";

const FormSchema = z.object({
  formTemplateId: z
    .string({ message: "This field is required" })
    .min(1, { message: "This field is required" }),
});
type FormType = z.infer<typeof FormSchema>;

const GET_SOURCE_FORM_TEMPLATES = gql`
  query GetFormTemplatesForRuleImport(
    $organizationId: uuid!
    $targetFormTemplateId: uuid!
  ) {
    form_template(
      where: {
        id: { _neq: $targetFormTemplateId }
        enrollment_period: { organization_id: { _eq: $organizationId } }
        deleted_at: { _is_null: true }
      }
    ) {
      id
      name
      updated_at
      enrollment_period {
        id
        name
      }
    }
  }
`;

type Props = {
  isOpen: boolean;
  onClose: () => void;
  targetFormTemplateId: string;
  organization: UseOrganizationType;
  onComplete: (errors: RuleImportError[]) => void;
};

export const ImportRulesFromOtherDialog: React.FC<Props> = ({
  isOpen,
  onClose,
  targetFormTemplateId,
  organization,
  onComplete,
}) => {
  const toast = useAvelaToast();

  const { remoteData } = useRemoteDataQuery<
    GQL.GetFormTemplatesForRuleImport,
    GQL.GetFormTemplatesForRuleImportVariables
  >(GET_SOURCE_FORM_TEMPLATES, {
    variables: {
      organizationId: organization.toNullable()?.id ?? "",
      targetFormTemplateId,
    },
    skip: !organization.hasData(),
    fetchPolicy: "cache-and-network",
  });

  const handleImportFromOther = React.useCallback(
    async (values: FormType) => {
      toast.closeAll();

      try {
        // Compose the two endpoints client-side: export from the source, then
        // import into the target.
        const exportResult = (
          await FSR.exportRuleConfiguration({
            path: { formTemplateId: values.formTemplateId },
          })
        ).data?.data;

        if (!exportResult) {
          toast.error({
            title: "Something went wrong",
            description: "Please try again",
          });
          return;
        }

        if (exportResult.result === "invalid") {
          toast.error({
            title: "The source form's rules could not be read",
            description: "Review the specific errors and try again",
          });
          return onComplete(buildRuleImportErrors(exportResult.errorGroups));
        }

        const rules = exportResult.rules;

        const validation = RuleImportExportSchema.safeParse(rules);
        if (!validation.success) {
          toast({
            title: "Some rules were not imported",
            description: "Review the specific errors and try again",
            status: "warning",
          });
          return onComplete(
            zodIssuesToRuleImportErrors(
              validation.error.issues,
              Array.isArray(rules) ? rules : []
            )
          );
        }

        const importResult = (
          await FSR.importRuleConfiguration({
            path: { formTemplateId: targetFormTemplateId },
            body: rules as FSR.ImportRuleConfigurationData["body"],
          })
        ).data?.data;

        if (!importResult) {
          toast.error({
            title: "Something went wrong",
            description: "Please try again",
          });
          return;
        }

        if (importResult.result === "invalid") {
          toast({
            title: "Some rules were not imported",
            description: "Review the specific errors and try again",
            status: "warning",
          });
          return onComplete(
            buildRuleImportErrors(importResult.errorGroups, rules)
          );
        }

        const sourceFormTemplate = remoteData
          .toNullable()
          ?.form_template.find((ft) => ft.id === values.formTemplateId);

        toast({
          title: "Imported from existing",
          description: sourceFormTemplate
            ? `Rules imported from ${sourceFormTemplate.name}`
            : "Rules imported",
          status: "info",
          isClosable: true,
        });
        return onComplete([]);
      } catch (error: any) {
        if (error?.error?.message) {
          toast.error({
            title: "Something went wrong",
            description: error.error.message,
          });
        } else {
          toast.genericError();
        }
      } finally {
        onClose();
      }
    },
    [onClose, onComplete, remoteData, targetFormTemplateId, toast]
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <Formik<FormType>
        initialValues={{ formTemplateId: "" }}
        onSubmit={handleImportFromOther}
        validate={validateWithZod(FormSchema)}
      >
        {(formik) => (
          <ModalContent as={Form} noValidate>
            <ModalHeader>Import rules</ModalHeader>
            <ModalBody>
              <Flex direction="column" gap="4">
                <Text>
                  Select a form to import rules from it.{" "}
                  <Box fontWeight="bold" as="span">
                    The import will update rules that share a key.
                  </Box>
                </Text>
                <RemoteDataView
                  remoteData={remoteData}
                  error={(error) => <GenericError error={error} />}
                  loading={<Skeleton width="100%" height="2rem" />}
                >
                  {(data) => (
                    <FormikSearchSelect
                      name="formTemplateId"
                      label="Form to import rules from"
                      items={data.form_template.map((ft) => ({
                        key: ft.id,
                        label: ft.name,
                        description: (
                          <>
                            <Text>{ft.enrollment_period.name}</Text>
                            <Text>
                              Last modified on{" "}
                              {formatIsoDateToOrgDate(
                                ft.updated_at,
                                organization
                              )}
                            </Text>
                          </>
                        ),
                      }))}
                    />
                  )}
                </RemoteDataView>
              </Flex>
            </ModalBody>
            <ModalFooter gap="3">
              <Button
                variant="ghost"
                onClick={onClose}
                isDisabled={formik.isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" isLoading={formik.isSubmitting}>
                Import
              </Button>
            </ModalFooter>
          </ModalContent>
        )}
      </Formik>
    </Modal>
  );
};
